import React from 'react';
import { useSelector } from 'react-redux';
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import 'react-perfect-scrollbar/dist/css/styles.css';
import SeverityTag from '../SeverityTag';
import { formatUtcDate } from '../../helpers/date-utils';
import './DetailsTable.scss';
import { coy } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import Markdown from 'react-markdown';
import DOMPurify from 'dompurify';

const DetailsTable = (props) => {
    const hideFirstLine = props.hideFirstLine || false;
    const objectDetails = props.objectDetails || {};
    const isChild = props.isChild;
    const user = useSelector((state) => state.Auth.user);
    const toolNameMapping = props.toolNameMapping || {};

    const getKeyValue = (key, value) => {
        if (['created', 'updated', 'entry_date', 'last_found_at', 'published_date', 'last_modified_date'].includes(key)) {
            return formatUtcDate(value);
        }
        if (key === 'severity_level') {
            return <SeverityTag value={objectDetails[key]} />;
        }
        if (['diff', 'code_section', 'code_block'].includes(key)) {
            const showLineNumbers = objectDetails['line'] ? true : false;
            return (
                <SyntaxHighlighter
                    showLineNumbers={showLineNumbers}
                    wrapLines={true}
                    style={coy}
                    lineProps={(lineNumber) => {
                        const style = { display: 'block', width: 'fit-content' };
                        if (objectDetails['line'] > 0 && objectDetails['line'] === lineNumber) {
                            style.background = '#fcfc29';
                        }
                        return { style };
                    }}>
                    {value}
                </SyntaxHighlighter>
            ); //todo add language
        }

        if (['request', 'response'].includes(key) && value !== '') {
            const parsedValue = value.replace(/\r\n/gi, "\n").replace(/\n/gi, "  \n  ");
            return <Markdown className="custom-markdown">{parsedValue}</Markdown>;
        }

        if (['recommendation', 'issue_background'].includes(key)) {
            return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />;
        }
        if (['code_example'].includes(key)) {
            delete value.id;
            return <DetailsTable objectDetails={value} isChild={true} hideFirstLine={true} />;
        }
        if (key === 'active') {
            return value ? 'True' : 'False';
        }
        if (typeof value == 'boolean') {
            return value ? 'True' : 'False';
        }
        if (typeof value == 'object') {
            if (Array.isArray(value) && !isChild) {
                if (typeof value[0] == 'object') {
                    return (
                        <div>
                            {value.map((entry, idx) => {
                                return <DetailsTable objectDetails={entry} isChild={true} hideFirstLine={idx === 0} />;
                            })}
                        </div>
                    );
                }
            }

            return <pre>{JSON.stringify(value, null, 2)}</pre>;
        } else {
            return value;
        }
    };

    const getKeyFormatted = (key) => {
        const customLabels = { burpevidence_set: 'Evidence' };
        if (customLabels[key]) {
            return customLabels[key];
        }

        const label = key.replace(/_/g, ' ');
        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    const createTableEntries = () => {
        let ignoreKeys = [
            'id',
            'general_issue',
            'severity_level',
            'severity',
            'recommendation',
            'description',
            'category',
            'plugin',
            'title',
            'status',
            'message',
            'entry_date',
            'accepted_user',
            'accepted_history',
        ];

        let vals = [];

        // Add tool name for IU users if available
        if (user.role === 'IU' && objectDetails.general_issue.id && toolNameMapping[objectDetails.general_issue.id]) {
            vals.push(
                <div className="detail-item" key="tool_name">
                    <div className="title">
                        <h6>Tool name</h6>
                    </div>
                    <div className="description">{toolNameMapping[objectDetails.general_issue.id]}</div>
                </div>
            );
        }

        // Add last_found_at entry first if it exists
        if (objectDetails.hasOwnProperty('last_found_at') && objectDetails['last_found_at']) {
            vals.push(
                <div className="detail-item" key="last_found_at">
                    <div className="title">
                        <h6>{getKeyFormatted('last_found_at')}</h6>
                    </div>
                    <div className="description">{getKeyValue('last_found_at', objectDetails['last_found_at'])}</div>
                </div>
            );
        }

        if (user.role !== 'IU') {
            ignoreKeys = ignoreKeys.concat(['false_positive']);
        }

        for (let key in objectDetails) {
            let value = objectDetails[key];

            if (objectDetails.hasOwnProperty(key) && value)
                if (typeof value == 'object')
                    if (Object.keys(value).length === 0 || (Array.isArray(value) && value.length === 0))
                        if (!ignoreKeys.includes(key))
                            /**
                             * If value is an object or an array and it's empty, we add it to the ignoreKeys array (if the key doesn't exist there already)
                             */
                            ignoreKeys.push(key);

            if ((!ignoreKeys.includes(key) || isChild) && key !== 'last_found_at') { // Skip last_found_at here
                if (objectDetails.hasOwnProperty(key) && value) {
                    vals.push(
                        <div className="detail-item" key={key}>
                            <div className="title">
                                <h6>{getKeyFormatted(key)}</h6>
                            </div>
                            <div className="description">{getKeyValue(key, value)}</div>
                        </div>
                    );
                }
            }
        }

        return vals;
    };

    if (Object.entries(objectDetails).length === 0) {
        return <div />;
    }

    return <div className={`details-table ${hideFirstLine ? 'hide-top-border' : ''}`}>{createTableEntries()}</div>;
};

export default DetailsTable;
